// S2-0f.1-S1: Agent Sidebar — collapsible left rail with agent list, health dots, search
import { useState, useEffect } from 'react';
import { StatusDot } from './StatusDot';
import { Badge } from './Badge';
import { Input } from './Input';
import type { AgentListItem } from '../hooks/useAgents';
import './AgentSidebar.css';

const COLLAPSED_KEY = 'airgent-sidebar-collapsed';

export interface AgentSidebarProps {
  agents: AgentListItem[];
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  onSelect: (agentId: string) => void;
}

export function AgentSidebar({ agents, loading, error, selectedId, onSelect }: AgentSidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  });
  const [search, setSearch] = useState('');

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  // Filter agents by search query
  const filtered = search
    ? agents.filter(a => a.name.toLowerCase().includes(search.toLowerCase()))
    : agents;

  // Map health to StatusDot status prop
  const healthToStatus = (health: AgentListItem['health']): 'ready' | 'not-ready' | 'conditional' | 'evaluating' => {
    if (health === 'unknown') return 'evaluating'; // grey pulse = no data yet
    return health;
  };

  return (
    <aside className={`agent-sidebar${collapsed ? ' collapsed' : ''}`}>
      {/* Header: title + collapse toggle + search */}
      <div className="agent-sidebar-header">
        <div className="agent-sidebar-title-row">
          <span className="agent-sidebar-title">Agents</span>
          <button
            className="agent-sidebar-toggle"
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {/* Chevron SVG — points left when expanded, right when collapsed */}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d={collapsed ? 'M5 3L9 7L5 11' : 'M9 3L5 7L9 11'}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Search — hidden when collapsed */}
        <div className="agent-sidebar-search">
          <Input
            type="text"
            placeholder="Search agents..."
            value={search}
            onChange={setSearch}
          />
        </div>
      </div>

      {/* Agent list */}
      <div className="agent-sidebar-list">
        {loading && (
          <div className="agent-sidebar-status">Loading agents...</div>
        )}

        {error && (
          <div className="agent-sidebar-status agent-sidebar-error">{error}</div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="agent-sidebar-status">
            {search ? 'No agents match your search' : 'No agents registered'}
          </div>
        )}

        {filtered.map(agent => (
          <div
            key={agent.id}
            className={`agent-sidebar-item${selectedId === agent.id ? ' selected' : ''}`}
            onClick={() => onSelect(agent.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(agent.id); }}
            title={collapsed ? agent.name : undefined}
          >
            <StatusDot status={healthToStatus(agent.health)} />

            <div className="agent-sidebar-item-info">
              <div className="agent-sidebar-item-name">{agent.name}</div>
              <div className="agent-sidebar-item-meta">
                <Badge variant="neutral" size="sm">{agent.vertical}</Badge>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer: + New Agent stub */}
      <div className="agent-sidebar-footer">
        <button className="agent-sidebar-add" title="Agent creation coming soon" disabled>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2V10M2 6H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span className="agent-sidebar-add-label">New Agent</span>
        </button>
      </div>
    </aside>
  );
}
